import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";

import { HotelClinicStatus, IHotelClinicPassengerType } from "@/models/hotel-clinic.model";
import useClinicArrivalTaskDetailAssignation from "@/providers/hooks/useClinicArrivalTaskDetailAssignation";
import {
    emptyHotelClinicManagerTasksByDate,
    getHotelClinicManagerTasksByDateQueryKey,
    useGetHotelClinicManagerTasksByDate,
} from "@/requests/hotel-clinic.queries";
import { HotelClinicManagerTasksByDateResponseSchemaType } from "@/schemas/hotelClinicSchema";
import {
    ClinicArrivalDragPayload,
    ClinicArrivalDragPayloadTypes,
    ClinicArrivalOverTarget,
    ClinicArrivalOverTargetType,
} from "@/stores/use-clinic-arrival-dnd";

import { HotelClinicListItem, HotelClinicResolvedPassenger } from "../types";
import { HotelClinicCreateTaskPassengerPayload } from "../utils/hotelClinicCreateTaskPayload";

type UseClinicArrivalBoardResult = {
    data: HotelClinicManagerTasksByDateResponseSchemaType;
    isPending: boolean;
    date: DateTime;
    refetch: () => void;
    /**
     * Moves a passenger from `from` to `to`.
     * Both parameters are either "unassigned" or a task ID string.
     * No-op when source === destination or when the passenger is unknown.
     */
    movePassenger: ({
        dragData,
        target,
    }: {
        dragData: ClinicArrivalDragPayload;
        target: ClinicArrivalOverTarget;
    }) => void | Promise<void>;
};

function toResolved(p: HotelClinicListItem): HotelClinicResolvedPassenger {
    const base = {
        listItemId: `lt-${p.id}-${Date.now()}`,
        createdAt: new Date().toISOString(),
        status: HotelClinicStatus.PENDING,
        userId: null,
    };
    if (p.type === IHotelClinicPassengerType.CLINIC) {
        return { ...p, ...base };
    }
    return { ...p, ...base };
}

const invalidateQuery = (queryClient: QueryClient, isoDate: string) => {
    const queryKey = getHotelClinicManagerTasksByDateQueryKey(isoDate);
    queryClient.invalidateQueries({ queryKey });
};

const getPassenger = ({
    data,
    uniqueIdentifier,
    fromTasks,
}: {
    data: HotelClinicManagerTasksByDateResponseSchemaType;
    uniqueIdentifier: string;
    fromTasks: boolean;
}) => {
    if (!fromTasks) {
        return data.unassignedPassengers.find((p) => p.uniqueIdentifier === uniqueIdentifier);
    }
    let passenger: HotelClinicListItem | undefined;
    data.tasks.forEach((task) => {
        task.passengers.forEach((p) => {
            if (p.uniqueIdentifier === uniqueIdentifier) {
                passenger = p;
            }
        });
    });
    return passenger;
};

const passengerToCommonPassengerPayload = (p: HotelClinicListItem): HotelClinicCreateTaskPassengerPayload => {
    if (p.type === IHotelClinicPassengerType.CLINIC) {
        return { type: IHotelClinicPassengerType.CLINIC, pickUp: p.pickUp, operationId: p.id };
    }
    return { type: IHotelClinicPassengerType.CUSTOM, passengerDbId: p.id };
};

export function useClinicArrivalBoard({ date }: { date: DateTime }) {
    const queryClient = useQueryClient();
    const { data, isPending, refetch } = useGetHotelClinicManagerTasksByDate({ date });
    const isoDate = useMemo(() => date.toISODate()!, [date]);
    const queryKey = useMemo(() => getHotelClinicManagerTasksByDateQueryKey(isoDate), [isoDate]);

    const boardData: HotelClinicManagerTasksByDateResponseSchemaType = useMemo(
        () => data ?? emptyHotelClinicManagerTasksByDate,
        [data]
    );
    const { handleUnassignPassengersFromTask, assignationPending, handleAssignPassengersToTask } =
        useClinicArrivalTaskDetailAssignation({
            invalidateQuery: false,
        });

    const movePassenger = useCallback(
        async ({ dragData, target }: Parameters<UseClinicArrivalBoardResult["movePassenger"]>[0]) => {
            if (!dragData || !target || assignationPending) return;
            const fromTasks = dragData.type === ClinicArrivalDragPayloadTypes.FROM_TASK;
            const toTasks = target.type === ClinicArrivalOverTargetType.Task;
            if ((toTasks && fromTasks && target.taskId === dragData.taskId) || (!toTasks && !fromTasks)) return;

            const uniqueIdentifier = dragData.uniqueIdentifier;
            if (!uniqueIdentifier) return;

            if (fromTasks && target.type === ClinicArrivalOverTargetType.Unassigned && !dragData.listItemId) return;

            queryClient.setQueryData<HotelClinicManagerTasksByDateResponseSchemaType>(queryKey, (prev) => {
                const base = prev ?? emptyHotelClinicManagerTasksByDate;
                const passenger = getPassenger({ data: base, uniqueIdentifier, fromTasks });
                if (!passenger) return prev;

                let nextTasks = base.tasks;
                let nextUnassigned = base.unassignedPassengers;

                /** From tasks to unassigned */
                if (fromTasks) {
                    nextTasks = nextTasks
                        .map((task) =>
                            task.id === dragData.taskId
                                ? {
                                      ...task,
                                      passengers: task.passengers.filter(
                                          (p) => p.uniqueIdentifier !== uniqueIdentifier
                                      ),
                                  }
                                : task
                        )
                        .filter((task) => task.passengers.length);

                    handleUnassignPassengersFromTask({
                        taskId: dragData.taskId,
                        listItemIds: [dragData.listItemId],
                    })
                        .catch(() => {
                            invalidateQuery(queryClient, isoDate);
                        })
                        .then(() => {
                            if (toTasks) {
                                handleAssignPassengersToTask({
                                    taskId: target.taskId,
                                    passengers: [passengerToCommonPassengerPayload(passenger)],
                                }).catch(() => {
                                    invalidateQuery(queryClient, isoDate);
                                });
                            }
                        });
                } else {
                    /** From unassigned to tasks */
                    nextUnassigned = nextUnassigned.filter((p) => p.uniqueIdentifier !== uniqueIdentifier);
                }

                if (toTasks) {
                    if (!fromTasks) {
                        handleAssignPassengersToTask({
                            taskId: target.taskId,
                            passengers: [passengerToCommonPassengerPayload(passenger)],
                        }).catch(() => {
                            invalidateQuery(queryClient, isoDate);
                        });
                    }
                    // use assign ndpoint
                    /** From unassigned to tasks */
                    nextTasks = nextTasks.map((task) => {
                        if (task.id !== target.taskId) return task;
                        if (task.passengers.some((p) => p.uniqueIdentifier === uniqueIdentifier)) return task;
                        return { ...task, passengers: [...task.passengers, toResolved(passenger)] };
                    });
                } else {
                    // use assign endpoint
                    /** From tasks to unassigned */
                    if (!nextUnassigned.some((p) => p.uniqueIdentifier === uniqueIdentifier)) {
                        nextUnassigned = [...nextUnassigned, passenger];
                    }
                }

                return { tasks: nextTasks, unassignedPassengers: nextUnassigned };
            });
        },
        [
            queryClient,
            queryKey,
            handleUnassignPassengersFromTask,
            assignationPending,
            handleAssignPassengersToTask,
            isoDate,
        ]
    );

    return { data: boardData, isPending, movePassenger, date, refetch };
}
